import type { WalletPointsGetResult, WalletReferralsGetResult, } from "@bracket/bars/types";
import { API_URLS } from "@constants";
import * as G from "@globals";
import { loadAtTimeout, } from "@store/helpers";
import type { IBracketStore, } from "@store/types";
import type { Store, } from "@store/useStore";
import type { ImmerStateCreator } from "@types";
import { fetchApiResponseHandler } from "@utils";
import { wagmiConfig as config, } from "@wagmiconfig";
import { produce, } from "immer";
import { type GetAccountReturnType, getAccount, watchAccount, } from "wagmi/actions";
import type { IWalletReferralsSubSlice, } from "./types";

export const createWalletReferralsSlice: ImmerStateCreator<IBracketStore, IWalletReferralsSubSlice> = ( set, get ) => {


	const fetch = async () => {

		const { ready, fetching, } = get().referrals.walletReferrals;

		if(ready || fetching)
			return;

		if(_watcher) _watcher();

		_watcher = watchAccount(config, { onChange: _onAccountChange, });

		set(produce<Store>(state => {
			state.referrals.walletReferrals.ready = false;
			state.referrals.walletReferrals.fetching = true;
			state.referrals.walletReferrals.isLoading = true;
			state.referrals.walletReferrals.isError = false;
			state.referrals.walletReferrals.error = null;
		}));


		try {

			const { address, } = getAccount(config);

			if(!address)
				throw new Error("walletReferralsSlice called without a valid wallet set");

			const [
				points,
				adjustments,
				data,
			 ] = await Promise.all([
				fetchApiResponseHandler<WalletPointsGetResult>({
					url: `${API_URLS.BARS}/wallet/points?address=${address}`,
					options: { method: "GET", },
					signal: new AbortController().signal,
					onAbort: () => {
						set(produce<Store>(state => {
							state.referrals.walletReferrals.isLoaded = false;
							state.referrals.walletReferrals.isLoading = false;
						}));
					}
				}),
				fetchApiResponseHandler<Array<{ state: { tvl_points: number; }}>>({
					url: `${API_URLS.IDENT}/adjustments?wallet=${address}`,
					options: { method: "GET", },
					signal: new AbortController().signal,
					onAbort: () => {
						const { address, } = G.wallet.getBracketWallet();
						console.log("wallet adjustments request aborted --- ", address);
						set(produce<Store>(state => {
							state.referrals.walletReferrals.isLoaded = false;
							state.referrals.walletReferrals.isLoading = false;
						}));
					}
				}),
				fetchApiResponseHandler<WalletReferralsGetResult>({
					url: `${API_URLS.BARS}/wallet/referrals?address=${address}`,
					options: { method: "GET", },
					signal: new AbortController().signal,
					onAbort: () => {
						const { address, } = G.wallet.getBracketWallet();
						console.log("wallet referrals request aborted --- ", address);
						set(produce<Store>(state => {
							state.referrals.walletReferrals.isLoaded = false;
							state.referrals.walletReferrals.isLoading = false;
						}));
					}
				})
			]);

			const reassigned_points = adjustments.reduce((acc: number, adj) => acc + adj.state.tvl_points * 0.2, 0);

			const {
				total_points,
				xp,
				initial_points,
				ref_points,
			} = points.points;

			set(produce<Store>(state => {
				state.referrals.walletReferrals.ready = true;
				state.referrals.walletReferrals.fetching = false;
				state.referrals.walletReferrals.referral_points_earned = ref_points + reassigned_points;
				state.referrals.walletReferrals.total_points_earned = (total_points || 0) + (xp || 0) + (initial_points || 0);
				state.referrals.walletReferrals.total_points = total_points; // + ref_points + reassigned_points;
				state.referrals.walletReferrals.data = data;
				state.referrals.walletReferrals.points = points.points;
				state.referrals.walletReferrals.isLoading = false;
				state.referrals.walletReferrals.isLoaded = true;
			}));

		} catch(error) {
			set(produce<Store>(state => {
				state.referrals.walletReferrals.isLoading = false;
				state.referrals.walletReferrals.isError = true;
				state.referrals.walletReferrals.error = error instanceof Error ? error : new Error("An unknown error occurred");
			}));
		}
	}


	const refetch = () => {
		set(produce<Store>(state => {
			state.referrals.walletReferrals.isLoaded = false;
		}));
		fetch();
	};


	const update = (updater: (oldData: WalletReferralsGetResult | null) => WalletReferralsGetResult) => {
		set(produce<Store>(state => {
			state.referrals.walletReferrals.data = updater(state.referrals.walletReferrals.data);
		}));
	};


	const setData = (data: WalletReferralsGetResult) => { 	
		set(produce<Store>(state => {
			state.referrals.walletReferrals.data = data;
		}));
	};
	

	const setError = (error: Error) => {
		set(produce<Store>(state => {
			state.referrals.walletReferrals.isError = true;
			state.referrals.walletReferrals.error = error;
		}));
	};


	const _onAccountChange = (C: GetAccountReturnType, P:Partial<GetAccountReturnType>) => {
		if(C.address && C.isConnected) {
			if(C.address !== P.address) {
				refetch();
				return;
			}
		}
	}


	let _watcher: ReturnType<typeof watchAccount> | null = null;

	loadAtTimeout("referrals.walletReferrals", 3000);

	return {
		ready: false,
		fetching: false,
		data: null,
		points: null,
		referral_points_earned: null,
		total_points_earned: null,
		total_points: null,
		isLoading: false,
		isLoaded: false,
		isError: false,
		error: null,
		fetch,
		refetch,
		update,
		setData,
		setError,
	}

};
